import { useState } from 'react';
import { supabase } from './supabaseClient';

const OUTCOMES = ['Interested','Not interested','Ringing no response','Wrong number','Incorrect PoC','Call back'];

const STATUS_COLORS = {
  'New':                  { bg:'#EEEDFE', color:'#3C3489' },
  'Interested':           { bg:'#EAF3DE', color:'#27500A' },
  'Not interested':       { bg:'#FCEBEB', color:'#791F1F' },
  'Ringing no response':  { bg:'#F1EFE8', color:'#444441' },
  'Wrong number':         { bg:'#FCEBEB', color:'#791F1F' },
  'Incorrect PoC':        { bg:'#FAEEDA', color:'#633806' },
  'Call back':            { bg:'#FAEEDA', color:'#633806' },
};

const ILS_COLORS = {
  A: { bg:'#EAF3DE', color:'#27500A' },
  B: { bg:'#DBEAFE', color:'#0C447C' },
  C: { bg:'#FAEEDA', color:'#633806' },
  D: { bg:'#F1EFE8', color:'#444441' },
};

const EMAIL_TEMPLATES = [
  { name: 'Follow-up', subject: 'Following up on our conversation', body: 'Hi {name},\n\nI wanted to follow up on our recent discussion. Please let me know if you have any questions.\n\nBest regards,\n{repName}' },
  { name: 'Introduction', subject: 'Introduction to Dataneers', body: 'Hi {name},\n\nI\'m {repName} from Dataneers. We help businesses like yours with data solutions. Would you have 15 minutes for a quick chat?\n\nCheers,\n{repName}' },
  { name: 'Meeting request', subject: 'Schedule a meeting', body: 'Hi {name},\n\nCould we schedule a 30-minute call next week to discuss how Dataneers can assist {company}?\n\nLooking forward,\n{repName}' },
];

export default function LeadCard({ lead, repId, repEmail, onStatusChange }) {
  const [expanded,         setExpanded]         = useState(false);
  const [outcome,          setOutcome]          = useState('');
  const [note,             setNote]             = useState('');
  const [saving,           setSaving]           = useState(false);
  const [amitAlert,        setAmitAlert]        = useState(false);
  const [history,          setHistory]          = useState(lead.call_logs || []);
  const [showEmailModal,   setShowEmailModal]   = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const statusStyle = STATUS_COLORS[lead.status] || STATUS_COLORS['New'];
  const ilsColor    = ILS_COLORS[lead.ils_bucket] || ILS_COLORS['D'];

  function toggleExpand(e) {
    if (e.target.closest('.no-collapse')) return;
    setExpanded(x => !x);
  }

  async function saveLog() {
    if (!outcome) { alert('Please select an outcome.'); return; }
    setSaving(true);

    // Look up rep fresh from DB every time
    const { data: { session } } = await supabase.auth.getSession();
    const { data: rep } = await supabase
      .from('reps')
      .select('id, name')
      .eq('email', session.user.email)
      .single();

    if (!rep) {
      alert('Rep not found. Contact Amit.');
      setSaving(false);
      return;
    }

    const { error: logErr } = await supabase.from('call_logs').insert({
      lead_id:  lead.id,
      rep_id:   rep.id,       // ✅ guaranteed correct
      rep_name: rep.name,     // ✅ logged-in rep's name
      outcome,
      notes:    note || null,
    });
    if (logErr) { alert('Error saving: ' + logErr.message); setSaving(false); return; }

    const { error: leadErr } = await supabase
      .from('leads')
      .update({ status: outcome })
      .eq('id', lead.id);
    if (leadErr) { console.error(leadErr); }

    if (outcome === 'Interested') {
      await supabase.from('leads').update({ amit_alerted: true }).eq('id', lead.id);
      supabase.functions.invoke('alert-amit', { body: { lead_id: lead.id } }).catch(console.error);
      setAmitAlert(true);
    }

    const newLog = { outcome, notes: note, called_at: new Date().toISOString() };
    setHistory(h => [newLog, ...h]);
    onStatusChange(lead.id, outcome);
    setOutcome('');
    setNote('');
    setSaving(false);
  }

  function sendEmailWithTemplate() {
    if (!selectedTemplate) { alert('Please select a template'); return; }
    const template = EMAIL_TEMPLATES.find(t => t.name === selectedTemplate);
    if (!template) return;

    const repName = lead.assigned_rep_name || 'Sales Rep';
    const body = template.body
      .replace('{name}', lead.name.split(' ')[0])
      .replace('{company}', lead.organization || 'your organization')
      .replace(/{repName}/g, repName);

    const mailtoLink = `mailto:${lead.email || ''}?subject=${encodeURIComponent(template.subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    setShowEmailModal(false);
    setSelectedTemplate('');
  }

  return (
    <>
      <div style={{ ...s.card, ...(expanded ? s.cardExpanded : {}) }} onClick={toggleExpand}>
        <div style={s.top}>
          <div style={{ ...s.ilsBadge, background: ilsColor.bg, color: ilsColor.color }}>
            {lead.ils_score || '—'}
          </div>
          <div style={s.info}>
            <div style={s.name}>{lead.name}</div>
            <div style={s.company}>{lead.organization}{lead.designation ? ` · ${lead.designation}` : ''}</div>
          </div>
          <div style={s.right}>
            <span style={{ ...s.statusBadge, background: statusStyle.bg, color: statusStyle.color }}>
              {lead.status || 'New'}
            </span>
            {lead.phone && (
              <a href={`tel:${lead.phone}`} style={s.phone} className="no-collapse" onClick={e => e.stopPropagation()}>
                {lead.phone}
              </a>
            )}
            <button
              style={s.emailBtn}
              className="no-collapse"
              onClick={(e) => { e.stopPropagation(); setShowEmailModal(true); }}
            >
              📧 Email Now
            </button>
          </div>
        </div>

        {expanded && (
          <div style={s.body} className="no-collapse" onClick={e => e.stopPropagation()}>
            <div style={s.detailGrid}>
              {lead.email    && <><span style={s.dl}>Email</span><span style={s.dv}>{lead.email}</span></>}
              {lead.industry && <><span style={s.dl}>Industry</span><span style={s.dv}>{lead.industry}</span></>}
              {lead.location && <><span style={s.dl}>Location</span><span style={s.dv}>{lead.location}</span></>}
            </div>

            <div style={s.histTitle}>CALL HISTORY</div>
            {history.length === 0
              ? <div style={s.noHist}>No calls logged yet</div>
              : history.map((h, i) => (
                  <div key={i} style={s.histRow}>
                    <span style={s.histDate}>
                      {new Date(h.called_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                    </span>
                    <div>
                      <div style={{ ...s.histOutcome, color: h.outcome === 'Interested' ? '#3B6D11' : '#111827' }}>
                        {h.outcome}
                      </div>
                      {h.notes && <div style={s.histNote}>{h.notes}</div>}
                    </div>
                  </div>
                ))
            }

            {(amitAlert || lead.amit_alerted) && (
              <div style={s.amitBanner}>Amit and Sid have been alerted about this lead.</div>
            )}

            {!repId && (
              <div style={s.errorBanner}>
                ⚠️ Your account ({repEmail}) is not configured as a rep. Contact Amit to add your email to the reps table before logging calls.
              </div>
            )}

            <div style={s.logPanel}>
              <div style={s.logTitle}>Log a call</div>
              <div style={s.outcomeGrid}>
                {OUTCOMES.map(o => (
                  <button
                    key={o}
                    style={{
                      ...s.outcomeBtn,
                      ...(outcome === o
                        ? o === 'Interested'
                          ? s.outcomeBtnSelInt
                          : s.outcomeBtnSel
                        : {})
                    }}
                    onClick={() => { setOutcome(o); if(o==='Interested') setAmitAlert(false); }}
                  >
                    {o}
                  </button>
                ))}
              </div>

              <textarea
                style={s.noteArea}
                rows={2}
                placeholder="Add a note (optional)..."
                value={note}
                onChange={e => setNote(e.target.value)}
              />
              <button style={s.saveBtn} onClick={saveLog} disabled={saving}>
                {saving ? 'Saving...' : 'Save call log'}
              </button>
            </div>
          </div>
        )}
      </div>

      {showEmailModal && (
        <div style={modalStyles.overlay} onClick={() => setShowEmailModal(false)}>
          <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
            <div style={modalStyles.header}>Send Email to {lead.name}</div>
            <div style={modalStyles.body}>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                style={modalStyles.select}
              >
                <option value="">-- Choose a template --</option>
                {EMAIL_TEMPLATES.map(t => (
                  <option key={t.name} value={t.name}>{t.name}</option>
                ))}
              </select>
              <div style={modalStyles.actions}>
                <button style={modalStyles.cancelBtn} onClick={() => setShowEmailModal(false)}>Cancel</button>
                <button style={modalStyles.sendBtn} onClick={sendEmailWithTemplate}>Send Email</button>
              </div>
              <div style={modalStyles.note}>Note: This will open your default email client. Make sure you are logged into your @dataneers.in account.</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const s = {
  card:             { background:'#fff', border:'0.5px solid #E5E7EB', borderRadius:12, marginBottom:8, overflow:'hidden', cursor:'pointer', transition:'border-color .15s' },
  cardExpanded:     { border:'0.5px solid #D1D5DB' },
  top:              { display:'flex', alignItems:'center', gap:10, padding:'12px 14px' },
  ilsBadge:         { width:36, height:36, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:500, flexShrink:0 },
  info:             { flex:1, minWidth:0 },
  name:             { fontSize:13, fontWeight:500, color:'#111827', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  company:          { fontSize:12, color:'#6B7280', marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  right:            { display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 },
  statusBadge:      { fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:500, whiteSpace:'nowrap' },
  phone:            { fontSize:11, color:'#1B3A5C', textDecoration:'none', whiteSpace:'nowrap' },
  emailBtn:         { fontSize:11, background:'none', border:'0.5px solid #E5E7EB', borderRadius:6, padding:'2px 8px', cursor:'pointer', color:'#1B3A5C', marginTop:2 },
  body:             { borderTop:'0.5px solid #E5E7EB', padding:'12px 14px' },
  detailGrid:       { display:'grid', gridTemplateColumns:'80px 1fr', gap:'4px 8px', marginBottom:12, fontSize:12 },
  dl:               { color:'#6B7280' },
  dv:               { color:'#111827' },
  histTitle:        { fontSize:11, fontWeight:500, color:'#9CA3AF', letterSpacing:'0.05em', textTransform:'uppercase', marginBottom:6 },
  noHist:           { fontSize:12, color:'#9CA3AF', padding:'4px 0', marginBottom:12 },
  histRow:          { display:'flex', gap:8, alignItems:'flex-start', padding:'5px 0', borderBottom:'0.5px solid #F3F4F6', fontSize:12 },
  histDate:         { color:'#9CA3AF', minWidth:90, flexShrink:0 },
  histOutcome:      { fontWeight:500, color:'#111827' },
  histNote:         { color:'#6B7280', marginTop:2 },
  amitBanner:       { background:'#EAF3DE', border:'0.5px solid #97C459', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#27500A', margin:'10px 0' },
  errorBanner:      { background:'#FCEBEB', border:'0.5px solid #F5A0A0', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#791F1F', margin:'10px 0' },
  logPanel:         { background:'#F9FAFB', borderRadius:8, padding:'10px 12px', marginTop:12 },
  logTitle:         { fontSize:12, fontWeight:500, color:'#111827', marginBottom:8 },
  outcomeGrid:      { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginBottom:8 },
  outcomeBtn:       { padding:'6px 4px', borderRadius:8, border:'0.5px solid #E5E7EB', fontSize:11, cursor:'pointer', background:'#fff', color:'#6B7280', textAlign:'center', transition:'all .15s', lineHeight:1.3 },
  outcomeBtnSel:    { borderColor:'#1B3A5C', background:'#DBEAFE', color:'#1B3A5C', fontWeight:500 },
  outcomeBtnSelInt: { borderColor:'#3B6D11', background:'#EAF3DE', color:'#27500A', fontWeight:500 },
  noteArea:         { width:'100%', fontSize:12, padding:'7px 10px', borderRadius:8, border:'0.5px solid #E5E7EB', background:'#fff', color:'#111827', resize:'none', fontFamily:'inherit', marginBottom:8, boxSizing:'border-box' },
  saveBtn:          { width:'100%', padding:9, borderRadius:8, border:'none', background:'#1B3A5C', color:'#fff', fontSize:13, cursor:'pointer', fontWeight:500 },
};

const modalStyles = {
  overlay:   { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal:     { background:'#fff', borderRadius:16, width:'90%', maxWidth:450, padding:20, boxShadow:'0 10px 25px rgba(0,0,0,0.1)' },
  header:    { fontSize:16, fontWeight:500, marginBottom:16 },
  body:      { display:'flex', flexDirection:'column', gap:16 },
  select:    { padding:10, borderRadius:8, border:'0.5px solid #E5E7EB', fontSize:14 },
  actions:   { display:'flex', justifyContent:'flex-end', gap:12 },
  cancelBtn: { padding:'8px 16px', background:'#f3f4f6', border:'none', borderRadius:8, cursor:'pointer' },
  sendBtn:   { padding:'8px 16px', background:'#1B3A5C', color:'#fff', border:'none', borderRadius:8, cursor:'pointer' },
  note:      { fontSize:11, color:'#9CA3AF', marginTop:8 },
};
